// Our Solutions — five core service offerings for TinkerShift.
// Each service has a slug (used in /solutions/<slug>), hero copy, deliverables,
// ideal customer, indicative investment and a CTA label.

export const SOLUTIONS = [
  {
    slug: "strategy",
    nav: "Makerspace Strategy",
    title: "Makerspace Strategy & Consulting",
    headline: "Your makerspace starts with a plan.",
    subheading:
      "Expert guidance to turn ideas, equipment and unused spaces into meaningful learning experiences.",
    description:
      "A successful makerspace is more than a room full of technology. It requires a clear vision, the right resources, confident educators and a learning framework that encourages curiosity, creativity and experimentation. TinkerShift works alongside schools, libraries and councils to design practical, sustainable makerspace strategies.",
    deliverables: [
      "Initial discovery and needs assessment",
      "Existing equipment and resource audit",
      "Makerspace layout and design recommendations",
      "Technology and equipment planning",
      "Learning pathway recommendations",
      "Staffing and educator capability review",
      "Budget and implementation priorities",
      "Practical implementation roadmap",
    ],
    ideal_for:
      "Schools, libraries and councils planning a new makerspace or improving an existing one.",
    investment: {
      from: 1500,
      note: "Final pricing depends on scope, site requirements and deliverables.",
    },
    cta: "Book a Makerspace Consultation",
    color: "#0055FF",
    text: "#FFFFFF",
    icon: "Compass",
    accent: "Strategy",
    number: "01",
  },
  {
    slug: "professional-development",
    nav: "Professional Development",
    title: "Educator Professional Development",
    headline: "Confident educators. Creative learners.",
    subheading:
      "Hands-on PD that empowers educators to facilitate learning through making, experimentation and play.",
    description:
      "You don’t need to be a robotics engineer or coding expert to facilitate meaningful makerspace learning. Our professional development sessions give teachers, librarians and learning facilitators the confidence, practical skills and resources to guide students through creative technology challenges. Our approach focuses on facilitation — not simply following instructions.",
    training_areas: [
      { name: "Coding", blurb: "Creative coding, Scratch and game design." },
      { name: "Robotics", blurb: "Hands-on robotics, programming and problem-solving." },
      { name: "3D Design + Printing", blurb: "Digital design, prototyping and fabrication." },
      { name: "Media + Animation", blurb: "Storytelling, stop-motion, filmmaking and digital creativity." },
      { name: "Bonus Technology", blurb: "Emerging and experimental technologies." },
    ],
    additional_topics: [
      "The TinkerTank / Julija makerspace methodology",
      "Learning through play and experimentation",
      "Student-led and project-based learning",
      "Encouraging creativity and resilience",
      "Managing open-ended makerspace challenges",
      "Designing inclusive, differentiated activities",
      "Assessing learning through student portfolios",
    ],
    delivery_options: [
      "Half-day workshops",
      "Full-day workshops",
      "Customised staff training",
      "Multi-session PD programs",
      "Ongoing mentoring",
    ],
    ideal_for: "Teachers, librarians, learning facilitators and whole-staff teams.",
    investment: {
      from: 950,
      per: "session",
      note: "Volume and multi-session rates on request.",
    },
    cta: "Explore Professional Development",
    color: "#FF007F",
    text: "#FFFFFF",
    icon: "Users2",
    accent: "PD",
    number: "02",
  },
  {
    slug: "launch",
    nav: "Launch & Activation",
    title: "Makerspace Launch & Activation",
    headline: "From empty space to active learning.",
    subheading:
      "We help you create a makerspace that actually gets used.",
    description:
      "Many organisations invest in technology but struggle to turn that investment into consistent, engaging learning experiences. TinkerShift provides end-to-end support to help schools and libraries establish, launch and activate their makerspaces — bringing together equipment planning, learning pathways, educator training, challenges and practical implementation support.",
    deliverables: [
      "Makerspace planning and design",
      "Equipment and technology recommendations",
      "Learning pathway implementation",
      "Starter challenge library",
      "Educator training",
      "Operational and session planning",
      "Launch workshops",
      "Initial implementation support",
      "Evaluation and improvement recommendations",
    ],
    add_ons: [
      "Maker kits and equipment",
      "Additional educator training",
      "Ongoing mentoring",
      "TinkerShift platform access when available",
    ],
    ideal_for:
      "Schools, libraries and councils launching or relaunching makerspaces.",
    investment: {
      from: 5000,
      note: "Equipment and major fit-out costs are quoted separately.",
    },
    cta: "Plan Your Makerspace Launch",
    color: "#FFD700",
    text: "#1A1A1A",
    icon: "Rocket",
    accent: "Launch",
    number: "03",
  },
  {
    slug: "platform",
    nav: "The Platform",
    title: "The TinkerShift Platform",
    headline: "Your makerspace. Your students. One connected learning platform.",
    subheading: "The digital companion to future-ready, hands-on learning.",
    description:
      "The TinkerShift Platform is being developed to help educators access challenges, manage classes, track student projects and document learning through digital portfolios. Designed around the TinkerTank methodology, it connects hands-on learning with meaningful evidence of student progress.",
    status: "In Development — Founding Partner Enquiries Open",
    planned_features: [
      "Five learning pathways",
      "Structured challenges across five levels",
      "Teacher challenge and resource library",
      "HOW-TO video tutorials",
      "Class and student management",
      "Student profiles and project portfolios",
      "Images and project evidence",
      "Student reflection tools",
      "Skills and achievement badges",
      "Teacher-created challenges",
      "Community content approval",
      "Organisation reporting",
      "Makerspace equipment tracking",
    ],
    pathways: [
      "Coding",
      "Robotics",
      "3D Design + Printing",
      "Media + Animation",
      "Bonus",
    ],
    ideal_for:
      "Schools, libraries and councils seeking a connected makerspace learning platform.",
    investment: {
      from: 1200,
      per: "year",
      note: "Proposed annual licence per organisation, subject to final product scope and licence terms.",
    },
    cta: "Become a Founding Partner",
    secondary_cta: "Explore the Platform",
    color: "#00C2B8",
    text: "#1A1A1A",
    icon: "LayoutDashboard",
    accent: "Platform",
    number: "04",
  },
  {
    slug: "resources",
    nav: "Resources & Kits",
    title: "Curriculum, Resources & Maker Kits",
    headline: "Everything you need to get making.",
    subheading:
      "Practical resources, creative challenges and curated maker kits for educators.",
    description:
      "Bring hands-on learning into your school or library with structured activities, practical teaching resources and technology-specific maker kits. Our resources are based on TinkerTank’s experience delivering creative technology learning in schools, libraries and community settings.",
    resource_categories: [
      "Coding challenges",
      "Robotics challenges",
      "3D design and printing projects",
      "Media and animation activities",
      "Bonus technology challenges",
      "Educator HOW-TO videos",
      "Printable challenge cards",
      "Facilitator guides",
      "Equipment and setup recommendations",
    ],
    packages: [
      "Coding Starter Pack",
      "Robotics Challenge Pack",
      "3D Design + Printing Pack",
      "Media + Animation Pack",
      "Complete Makerspace Resource Bundle",
    ],
    ideal_for:
      "Educators and schools looking for ready-to-run creative learning resources.",
    investment: {
      from: 350,
      per: "package",
      note: "Physical kits, equipment and delivery are priced separately.",
    },
    cta: "Explore Maker Resources",
    color: "#FF6600",
    text: "#FFFFFF",
    icon: "BookMarked",
    accent: "Resources",
    number: "05",
  },
];

export const ORG_TYPES = ["School", "Library", "Council", "Other"];

export const HOW_WE_WORK_STEPS = [
  {
    n: "01",
    title: "Discover",
    body:
      "We understand your organisation, goals, learners, equipment and challenges.",
    color: "#FFD700",
    icon: "Search",
  },
  {
    n: "02",
    title: "Design",
    body:
      "We create a practical learning and implementation plan tailored to your environment.",
    color: "#FF007F",
    icon: "Pencil",
  },
  {
    n: "03",
    title: "Equip",
    body:
      "We help identify the right resources, equipment and learning tools.",
    color: "#FF6600",
    icon: "Wrench",
  },
  {
    n: "04",
    title: "Empower",
    body:
      "We train educators and support the introduction of creative, hands-on learning.",
    color: "#0055FF",
    icon: "Sparkles",
  },
  {
    n: "05",
    title: "Sustain",
    body:
      "Ongoing resources, platform access where available, mentoring and continuous improvement.",
    color: "#00C2B8",
    icon: "Repeat",
  },
];
